import React, { useEffect, useState } from 'react'
import type { Editor } from '@tiptap/react'
import { Menu, ListItemText, Tooltip } from '@material-ui/core'
import MenuItem from '@paintscout/ui/components/MenuItem'
import { useTranslation } from '@paintscout/ui'
import type { Context } from '@paintscout/util/templater'
import type { TemplateTag } from '@paintscout/util/util'
import { insertTemplatePlaceholder, stringifyContextValue } from './insertTemplatePlaceholder'

export interface EditorTemplateMenuProps {
  editor: Editor | null
  /** Undefined/empty when the host editor has no template tags configured — renders nothing. */
  tags?: TemplateTag[]
  templateContext?: Context
  /** See `insertTemplatePlaceholder` — inserts a chip regardless of what `templateContext` resolves. */
  forcePlaceholder?: boolean
  tooltip?: string
  /** See `insertTemplatePlaceholder` — false preserves a non-empty selection instead of replacing it. */
  replaceSelection?: boolean
  /**
   * Close the menu when the editor's selection moves. Only for the transient tippy hosts (bubble /
   * floating menus), whose button disappears with the selection, leaving this Menu anchored to a
   * hidden node. The always-visible toolbar must NOT set this — its menu has always survived
   * selection changes.
   */
  closeOnSelectionUpdate?: boolean
}

/**
 * The resolved value shown under a tag's label. Shares `stringifyContextValue` with the insert
 * path, so what a menu item previews is exactly what picking it would insert.
 */
function previewValue(templateContext: Context | undefined, tag: TemplateTag): string | undefined {
  return stringifyContextValue(templateContext?.[tag.key])
}

/**
 * The "insert a `{{tag}}`" picker. Rendered as a plain `<button>` so it inherits the styling of
 * whichever menu container hosts it — the full toolbar, the bubble menu (text selected) or the
 * floating menu (empty line). All three share this one implementation.
 */
export function EditorTemplateMenu({
  editor,
  tags,
  templateContext,
  forcePlaceholder,
  tooltip,
  replaceSelection,
  closeOnSelectionUpdate
}: EditorTemplateMenuProps) {
  const { __ } = useTranslation()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const label = tooltip || __('Insert Placeholder')

  useEffect(() => {
    if (!editor || !closeOnSelectionUpdate) return
    const close = () => setAnchorEl(null)
    editor.on('selectionUpdate', close)
    return () => {
      editor.off('selectionUpdate', close)
    }
  }, [editor, closeOnSelectionUpdate])

  if (!tags?.length) return null

  return (
    <>
      <button
        type="button"
        // Keep focus (and the selection) in the editor — also stops Safari from dismissing the
        // bubble/floating menu out from under the click.
        onMouseDown={(ev) => ev.preventDefault()}
        // Opening happens on click, NOT mousedown. `click` is dispatched on the nearest common
        // ancestor of the mousedown and mouseup targets, so if the Menu's full-viewport modal root
        // mounts during mousedown, mouseup lands on *it* and the click re-targets all the way up to
        // <body>. The presentation editor wraps each section in a ClickAwayListener
        // (PresentationNext/Presentation/Presentation.tsx), and a click on <body> is outside both
        // the section's DOM and React's tree — so it reads as "clicked away", submits the form and
        // clears the focused section, collapsing the editor mid-edit.
        onClick={(ev) => setAnchorEl(ev.currentTarget)}
        aria-label={label}
      >
        <Tooltip title={label}>
          <i className="fa-solid fa-brackets-curly" />
        </Tooltip>
      </button>
      <Menu
        open={!!anchorEl}
        anchorEl={anchorEl}
        // Open below the button rather than over it. This replaces an inherited
        // `style={{ top: anchorEl?.offsetHeight }}`, which achieved the same thing by overriding
        // `top` on the Popover's full-viewport Modal root so the absolutely-positioned paper landed
        // one button-height lower — correct, but silently dependent on MUI keeping `top: 0` there,
        // and meaningless to read in a tippy-hosted menu.
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        onClose={() => setAnchorEl(null)}
        // yes, we need all 3 props to maintain focus on the editor when this is opened...
        autoFocus={false}
        disableAutoFocus
        disableAutoFocusItem
        disableEnforceFocus
      >
        {tags.map((tag) => (
          <MenuItem
            key={tag.key}
            // preventDefault keeps the editor's selection alive across the click; the insert itself
            // waits for `click` for the same re-targeting reason as the button above — tearing the
            // menu down during mousedown would move mouseup's target and bubble the click to <body>.
            onMouseDown={(ev) => ev.preventDefault()}
            onClick={() => {
              setAnchorEl(null)
              insertTemplatePlaceholder({ editor, tag, tags, templateContext, forcePlaceholder, replaceSelection })
            }}
          >
            <ListItemText
              primary={tag.label}
              secondary={tag.hideValue ? undefined : previewValue(templateContext, tag)}
            />
          </MenuItem>
        ))}
      </Menu>
    </>
  )
}
